import React, { useEffect, useState, useCallback } from "react";
import { api, mediaUrl } from "../api";
import { useData } from "../context/DataContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useToast } from "../hooks/use-toast";
import { LogOut, Plus, Pencil, Trash2, Upload, LayoutGrid, Package, Building2, Mail, FileText, BarChart3, ChevronUp, ChevronDown } from "lucide-react";
import { LOGO } from "../mock/mock";
import AdminDashboard from "../components/AdminDashboard";

const slugify = (s) => s.toString().toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// ---------- Reusable image field ----------
const ImageField = ({ value, onChange }) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const res = await api.upload(file);
      onChange(res.url);
    } catch (err) { toast({ title: "Upload failed" }); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-[#8b8271]">Image</Label>
      <div className="flex items-center gap-3 mt-1.5">
        {value ? <img src={mediaUrl(value)} alt="" className="w-16 h-16 object-cover rounded border border-[#e0d6c6]" /> : <div className="w-16 h-16 bg-[#efe7db] rounded" />}
        <div className="flex-1">
          <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="/mosaic/... or upload" className="mb-2" />
          <label className="inline-flex items-center gap-2 text-[12px] uppercase tracking-wider text-[#a67c52] cursor-pointer">
            <Upload size={14} /> {busy ? "..." : "Upload"}
            <input type="file" accept="image/*" onChange={handle} className="hidden" />
          </label>
        </div>
      </div>
    </div>
  );
};

// ---------- PDF / file field ----------
const FileField = ({ label = "PDF", value, onChange }) => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(false);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const res = await api.upload(file);
      onChange(res.url);
      toast({ title: "PDF uploaded" });
    } catch (err) { toast({ title: "Upload failed", description: "File may be too large" }); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-[#8b8271]">{label}</Label>
      <div className="flex items-center gap-3 mt-1.5">
        <span className={`w-10 h-12 flex items-center justify-center rounded border ${value ? "border-[#a67c52] text-[#a67c52]" : "border-[#e0d6c6] text-[#c4b29a]"}`}><FileText size={20} /></span>
        <div className="flex-1">
          <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="/catalog/... or upload a PDF" className="mb-2" />
          <div className="flex items-center gap-4">
            <label className="inline-flex items-center gap-2 text-[12px] uppercase tracking-wider text-[#a67c52] cursor-pointer">
              <Upload size={14} /> {busy ? "..." : "Upload PDF"}
              <input type="file" accept="application/pdf,.pdf" onChange={handle} className="hidden" />
            </label>
            {value && <button type="button" onClick={() => onChange("")} className="text-[12px] uppercase tracking-wider text-[#8b8271] hover:text-red-600">Remove</button>}
          </div>
          <p className="text-[11px] text-[#a89c88] mt-1">Karta tıklanınca bu PDF açılır (görsel/yazı sınırı yok).</p>
        </div>
      </div>
    </div>
  );
};


const Bi = ({ label, value, onChange, textarea }) => {
  const Comp = textarea ? Textarea : Input;
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-[#8b8271]">{label}</Label>
      <div className="grid grid-cols-2 gap-3 mt-1.5">
        <Comp value={value?.tr || ""} onChange={(e) => onChange({ ...value, tr: e.target.value })} placeholder="Türkçe" />
        <Comp value={value?.en || ""} onChange={(e) => onChange({ ...value, en: e.target.value })} placeholder="English" />
      </div>
    </div>
  );
};

// ---------- Login ----------
const LoginScreen = ({ onLogin }) => {
  const { toast } = useToast();
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.login(u, p);
      localStorage.setItem("haas_admin_token", res.token);
      onLogin();
    } catch (err) { toast({ title: "Invalid credentials" }); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#211d18] px-5">
      <form onSubmit={submit} className="bg-[#f7f3ee] p-10 w-full max-w-md">
        <img src={LOGO} alt="HAAS" className="h-12 mx-auto mb-8" />
        <h1 className="font-serif text-3xl text-center text-[#211d18] mb-8">Admin Panel</h1>
        <Label className="text-xs uppercase tracking-wider text-[#8b8271]">Username</Label>
        <Input value={u} onChange={(e) => setU(e.target.value)} className="mt-1.5 mb-4" />
        <Label className="text-xs uppercase tracking-wider text-[#8b8271]">Password</Label>
        <Input type="password" value={p} onChange={(e) => setP(e.target.value)} className="mt-1.5 mb-6" />
        <Button type="submit" disabled={busy} className="w-full bg-[#a67c52] hover:bg-[#8f6844] text-white uppercase tracking-wider">{busy ? "..." : "Sign In"}</Button>
      </form>
    </div>
  );
};

const Row = ({ img, title, sub, onEdit, onDelete, onUp, onDown, upDisabled, downDisabled }) => (
  <div className="flex items-center gap-4 bg-white border border-[#e6ddd0] px-4 py-3 mb-2">
    {(onUp || onDown) && (
      <div className="flex flex-col -my-1">
        <button onClick={onUp} disabled={upDisabled} data-testid="reorder-up" className="p-0.5 text-[#8b8271] hover:text-[#a67c52] disabled:opacity-25 disabled:cursor-not-allowed"><ChevronUp size={16} /></button>
        <button onClick={onDown} disabled={downDisabled} data-testid="reorder-down" className="p-0.5 text-[#8b8271] hover:text-[#a67c52] disabled:opacity-25 disabled:cursor-not-allowed"><ChevronDown size={16} /></button>
      </div>
    )}
    {img !== undefined && (img ? <img src={mediaUrl(img)} alt="" className="w-12 h-12 object-cover rounded" /> : <div className="w-12 h-12 bg-[#efe7db] rounded" />)}
    <div className="flex-1 min-w-0">
      <p className="font-medium text-[#211d18] truncate">{title}</p>
      {sub && <p className="text-[12px] text-[#8b8271] truncate">{sub}</p>}
    </div>
    {onEdit && <button onClick={onEdit} className="p-2 text-[#5a5348] hover:text-[#a67c52]"><Pencil size={16} /></button>}
    {onDelete && <button onClick={onDelete} className="p-2 text-[#5a5348] hover:text-red-600"><Trash2 size={16} /></button>}
  </div>
);

const empties = {
  category: () => ({ slug: "", name: { tr: "", en: "" }, tagline: { tr: "", en: "" }, desc: { tr: "", en: "" }, image: "", order: 99 }),
  product: () => ({ slug: "", name: "", category: "", image: "", description: { tr: "", en: "" }, pdf_tr: "", pdf_en: "", order: 99 }),
  project: () => ({ slug: "", name: { tr: "", en: "" }, location: { tr: "", en: "" }, image: "", pdf: "", order: 99 }),
};

const Admin = () => {
  const { toast } = useToast();
  const { refreshCategories } = useData();
  const [authed, setAuthed] = useState(!!localStorage.getItem("haas_admin_token"));
  const [cats, setCats] = useState([]);
  const [prods, setProds] = useState([]);
  const [projs, setProjs] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [editing, setEditing] = useState(null); // {type, data}

  const load = useCallback(async () => {
    try {
      const [c, p, pr] = await Promise.all([api.categories(), api.products(), api.projects()]);
      setCats(c); setProds(p); setProjs(pr);
      api.messages().then(setMsgs).catch(() => {});
    } catch (e) {}
  }, []);

  // Validate stored token on mount; expired/invalid -> force re-login
  useEffect(() => {
    if (!authed) return;
    api.me().catch(() => {
      localStorage.removeItem("haas_admin_token");
      setAuthed(false);
    });
  }, [authed]);

  // Global 401 handler (fired by axios interceptor)
  useEffect(() => {
    const onUnauth = () => setAuthed(false);
    window.addEventListener("haas-unauthorized", onUnauth);
    return () => window.removeEventListener("haas-unauthorized", onUnauth);
  }, []);

  useEffect(() => { if (authed) load(); }, [authed, load]);

  const logout = () => { localStorage.removeItem("haas_admin_token"); setAuthed(false); };

  const save = async () => {
    const { type, data } = editing;
    const payload = { ...data };
    if (!payload.slug) payload.slug = slugify(typeof payload.name === "string" ? payload.name : payload.name?.en || payload.name?.tr || "item");
    try {
      const map = {
        category: [api.createCategory, api.updateCategory],
        product: [api.createProduct, api.updateProduct],
        project: [api.createProject, api.updateProject],
      };
      const [create, update] = map[type];
      if (data.id) await update(data.id, payload); else await create(payload);
      toast({ title: "Saved" });
      setEditing(null);
      await load();
      if (type === "category") refreshCategories();
    } catch (e) { toast({ title: "Save failed", description: e?.response?.data?.detail || "" }); }
  };

  const del = async (type, id) => {
    if (!window.confirm("Delete this item?")) return;
    const map = { category: api.deleteCategory, product: api.deleteProduct, project: api.deleteProject };
    try { await map[type](id); await load(); if (type === "category") refreshCategories(); } catch (e) { toast({ title: "Delete failed" }); }
  };

  const moveProject = async (index, dir) => {
    const j = index + dir;
    if (j < 0 || j >= projs.length) return;
    const arr = [...projs];
    [arr[index], arr[j]] = [arr[j], arr[index]];
    const normalized = arr.map((p, i) => ({ ...p, order: i }));
    setProjs(normalized); // optimistic
    try {
      const changed = normalized.filter((p) => (projs.find((x) => x.id === p.id)?.order) !== p.order);
      await Promise.all(changed.map((p) => api.updateProject(p.id, p)));
    } catch (e) {
      toast({ title: "Sıralama kaydedilemedi" });
    } finally {
      await load();
    }
  };

  if (!authed) return <LoginScreen onLogin={() => setAuthed(true)} />;

  const d = editing?.data;
  const set = (patch) => setEditing((e) => ({ ...e, data: { ...e.data, ...patch } }));

  return (
    <div className="min-h-screen bg-[#efe7db]">
      <header className="bg-[#211d18] text-white">
        <div className="max-w-[1100px] mx-auto px-5 py-4 flex items-center justify-between">
          <img src={LOGO} alt="HAAS" className="h-9 brightness-0 invert" />
          <div className="flex items-center gap-4">
            <a href="/" className="text-[12px] uppercase tracking-wider text-white/70 hover:text-white">View Site</a>
            <button onClick={logout} className="inline-flex items-center gap-2 text-[12px] uppercase tracking-wider text-white/70 hover:text-white"><LogOut size={15} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-[1100px] mx-auto px-5 py-8">
        <Tabs defaultValue="dashboard">
          <TabsList className="mb-6">
            <TabsTrigger value="dashboard"><BarChart3 size={15} className="mr-1.5" /> Raporlar</TabsTrigger>
            <TabsTrigger value="products"><Package size={15} className="mr-1.5" /> Products</TabsTrigger>
            <TabsTrigger value="collections"><LayoutGrid size={15} className="mr-1.5" /> Collections</TabsTrigger>
            <TabsTrigger value="projects"><Building2 size={15} className="mr-1.5" /> Projects</TabsTrigger>
            <TabsTrigger value="messages"><Mail size={15} className="mr-1.5" /> Messages</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
            <AdminDashboard />
          </TabsContent>

          <TabsContent value="products">
            <div className="flex justify-end mb-4"><Button onClick={() => setEditing({ type: "product", data: empties.product() })} className="bg-[#a67c52] hover:bg-[#8f6844] text-white"><Plus size={16} className="mr-1" /> Add Product</Button></div>
            {prods.map((p) => <Row key={p.id} img={p.image} title={p.name} sub={p.category} onEdit={() => setEditing({ type: "product", data: p })} onDelete={() => del("product", p.id)} />)}
          </TabsContent>

          <TabsContent value="collections">
            <div className="flex justify-end mb-4"><Button onClick={() => setEditing({ type: "category", data: empties.category() })} className="bg-[#a67c52] hover:bg-[#8f6844] text-white"><Plus size={16} className="mr-1" /> Add Collection</Button></div>
            {cats.map((c) => <Row key={c.id} img={c.image} title={c.name?.tr || c.name?.en} sub={c.slug} onEdit={() => setEditing({ type: "category", data: c })} onDelete={() => del("category", c.id)} />)}
          </TabsContent>

          <TabsContent value="projects">
            <div className="flex items-center justify-between mb-4">
              <p className="text-[12px] text-[#8b8271]">Sırayı değiştirmek için ok tuşlarını kullanın. Üstteki en başta görünür.</p>
              <Button onClick={() => setEditing({ type: "project", data: { ...empties.project(), order: projs.length ? Math.max(...projs.map((p) => p.order || 0)) + 1 : 0 } })} className="bg-[#a67c52] hover:bg-[#8f6844] text-white"><Plus size={16} className="mr-1" /> Add Project</Button>
            </div>
            {projs.map((p, i) => (
              <Row
                key={p.id}
                img={p.image}
                title={p.name?.tr || p.name?.en}
                sub={p.location?.tr}
                onEdit={() => setEditing({ type: "project", data: p })}
                onDelete={() => del("project", p.id)}
                onUp={() => moveProject(i, -1)}
                onDown={() => moveProject(i, 1)}
                upDisabled={i === 0}
                downDisabled={i === projs.length - 1}
              />
            ))}
          </TabsContent>

          <TabsContent value="messages">
            {msgs.length === 0 && <p className="text-[#8b8271]">No messages yet.</p>}
            {msgs.map((m) => (
              <div key={m.id} className="bg-white border border-[#e6ddd0] p-4 mb-2">
                <div className="flex justify-between"><p className="font-medium text-[#211d18]">{m.name}</p><p className="text-[12px] text-[#8b8271]">{new Date(m.created_at).toLocaleString()}</p></div>
                <p className="text-[13px] text-[#5a5348]">{m.email} · {m.phone}</p>
                <p className="text-[14px] text-[#211d18] mt-2">{m.message}</p>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </div>

      {/* Editor dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{d?.id ? "Edit" : "Add"} {editing?.type}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              {editing.type === "product" && (
                <>
                  <div><Label className="text-xs uppercase tracking-wider text-[#8b8271]">Name</Label><Input value={d.name} onChange={(e) => set({ name: e.target.value })} className="mt-1.5" /></div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-[#8b8271]">Collection</Label>
                    <select value={d.category} onChange={(e) => set({ category: e.target.value })} className="mt-1.5 w-full border border-[#e0d6c6] bg-white px-3 py-2 text-sm">
                      <option value="">— select —</option>
                      {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name?.tr || c.name?.en}</option>)}
                    </select>
                  </div>
                  <Bi label="Description" value={d.description} onChange={(v) => set({ description: v })} textarea />
                  <FileField label="Türkçe Katalog (PDF)" value={d.pdf_tr} onChange={(v) => set({ pdf_tr: v })} />
                  <FileField label="İngilizce Katalog (PDF)" value={d.pdf_en} onChange={(v) => set({ pdf_en: v })} />
                </>
              )}
              {editing.type === "category" && (
                <>
                  <Bi label="Name" value={d.name} onChange={(v) => set({ name: v })} />
                  <Bi label="Tagline" value={d.tagline} onChange={(v) => set({ tagline: v })} />
                  <Bi label="Description" value={d.desc} onChange={(v) => set({ desc: v })} textarea />
                </>
              )}
              {editing.type === "project" && (
                <>
                  <Bi label="Name" value={d.name} onChange={(v) => set({ name: v })} />
                  <Bi label="Location" value={d.location} onChange={(v) => set({ location: v })} />
                </>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase tracking-wider text-[#8b8271]">Slug (optional)</Label><Input value={d.slug} onChange={(e) => set({ slug: e.target.value })} className="mt-1.5" /></div>
                <div><Label className="text-xs uppercase tracking-wider text-[#8b8271]">Order</Label><Input type="number" value={d.order} onChange={(e) => set({ order: parseInt(e.target.value || "0") })} className="mt-1.5" /></div>
              </div>
              <ImageField value={d.image} onChange={(v) => set({ image: v })} />
              {editing.type === "project" && (
                <FileField label="Proje PDF (Katalog)" value={d.pdf} onChange={(v) => set({ pdf: v })} />
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} className="bg-[#a67c52] hover:bg-[#8f6844] text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
